import React, { useMemo } from 'react';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';

import { useAppSelector } from '../../app/hooks';
import { BestPracticeStatus } from '../../interfaces/survey.interface';
import { getAssessmentQuestionsAndAnswersCount, getSurveyQuestionsAndAnswersCount } from '../../services/surveyHelpers';
import { useParams } from 'react-router-dom';
import { PRE_ASSESSMENT_STATUSES, WORKFLOW_STATUSES, WorkflowStatus } from '../../services/workflow';

type SurveyAssessmentProgressProps = {
  onClick?: () => void;
};

const SurveyAssessmentProgress = ({ onClick }: SurveyAssessmentProgressProps) => {
  const { surveyId } = useParams();
  const { survey } = useAppSelector((state) => state.surveys);
  const { assessment } = useAppSelector((state) => state.assessments);
  const { workflows, workflow } = useAppSelector((state) => state.workflow);
  const { assessmentStatus, assessmentQuestionsStatus, questionnaire, bpStatus } = useAppSelector(
    (state) => state.questionnaireAnswer,
  );

  const activeWorkflow = useMemo(() => workflows ? workflows.find((wf) => wf.isActive && wf.bpSurvey == surveyId) : workflow, [workflows, surveyId, workflow]);
  const isPreAssessment = useMemo(() => workflow && PRE_ASSESSMENT_STATUSES.includes(workflow.status as WorkflowStatus), [activeWorkflow]);

  const showProgressBar = () => {
    if (isPreAssessment) return true;
  
    if (assessmentStatus) {
      return assessmentStatus.toLowerCase() === 'assessing';
    }

    return survey && survey.status !== 'submittedForAssessment';
  };


  const progressValues = useMemo(() => {
    if (!showProgressBar() || (!assessment && !survey)) return;
    let questionsCount = 0;
    let answersCount = 0;
    if (assessment && activeWorkflow?.status !== WORKFLOW_STATUSES.PRE_ASSESSMENT_INCOMPLETE) {
      const { questionsCount: qCount, answersCount: aCount } = getAssessmentQuestionsAndAnswersCount(
        bpStatus,
        assessmentQuestionsStatus,
        questionnaire,
        activeWorkflow?.status as WorkflowStatus,
      );
      questionsCount = qCount;
      answersCount = aCount;
      return {
        answersCount,
        questionsCount,
        percent: (answersCount / questionsCount) * 100,
      };
    }

    const { questionsCount: sqCount, answersCount: saCount } = getSurveyQuestionsAndAnswersCount(
      survey?.bestPracticeStatuses as BestPracticeStatus[],
    );
    questionsCount = sqCount;
    answersCount = saCount;

    return {
      answersCount,
      questionsCount,
      percent: (answersCount / questionsCount) * 100,
    };
  }, [showProgressBar, assessment, survey, assessmentQuestionsStatus, questionnaire, bpStatus, surveyId, activeWorkflow, isPreAssessment]);

  if (!showProgressBar()) return null;

  return (
    <>
    <Box sx={{ maxWidth: '90%', mx: 'auto'  }}>
      {  activeWorkflow?.status === WORKFLOW_STATUSES.PRE_ASSESSMENT_INCOMPLETE && (
        <Typography variant="body1" gutterBottom sx={{ display: 'block', fontWeight: 300 }}>
        Questionnaire
        </Typography>
      )}
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', maxWidth: '90%', mx: 'auto', columnGap: '0.5rem' }}>
      <Box sx={{ position: 'relative', flexGrow: 1 }}>
        <Tooltip title={`${progressValues?.percent.toFixed()}%`}>
          <LinearProgress
            variant='determinate'
            color='primary'
            value={progressValues?.percent}
            onClick={onClick}
            sx={{ height: '1.25rem', borderRadius: '1rem', cursor: 'pointer' }}
          />
        </Tooltip>
      </Box>
      <Typography fontWeight='500'>{`${progressValues?.answersCount}/${progressValues?.questionsCount}`}</Typography>
      </Box>
    </>
  );
};

export default SurveyAssessmentProgress;
